import { useCallback, useEffect, useRef } from "react";

// Adapted from React Bits' <ClickSpark />: a full-screen canvas that bursts pixel sparks
// wherever you click. Square sparks keep it on-theme with the chunky 90s UI.

interface Spark {
  x: number;
  y: number;
  angle: number;
  startTime: number;
}

interface ClickSparkProps {
  sparkColor?: string;
  sparkSize?: number;
  sparkRadius?: number;
  sparkCount?: number;
  duration?: number;
  children: React.ReactNode;
}

const ClickSpark = ({
  sparkColor = "#fff",
  sparkSize = 4,
  sparkRadius = 22,
  sparkCount = 8,
  duration = 420,
  children,
}: ClickSparkProps) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const sparksRef = useRef<Spark[]>([]);
  const frameRef = useRef<number | null>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resize();
    window.addEventListener("resize", resize);
    return () => window.removeEventListener("resize", resize);
  }, []);

  const draw = useCallback(
    (now: number) => {
      const canvas = canvasRef.current;
      const ctx = canvas?.getContext("2d");
      if (!canvas || !ctx) return;
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      sparksRef.current = sparksRef.current.filter((spark) => {
        const progress = (now - spark.startTime) / duration;
        if (progress >= 1) return false;
        const eased = 1 - Math.pow(1 - progress, 3);
        const distance = eased * sparkRadius;
        const size = sparkSize * (1 - progress * 0.6);
        const x = spark.x + distance * Math.cos(spark.angle);
        const y = spark.y + distance * Math.sin(spark.angle);
        ctx.fillStyle = sparkColor;
        ctx.globalAlpha = 1 - progress;
        ctx.fillRect(
          Math.round(x - size / 2),
          Math.round(y - size / 2),
          size,
          size
        );
        return true;
      });
      ctx.globalAlpha = 1;

      if (sparksRef.current.length > 0) {
        frameRef.current = requestAnimationFrame(draw);
      } else {
        frameRef.current = null;
      }
    },
    [duration, sparkColor, sparkRadius, sparkSize]
  );

  useEffect(() => {
    return () => {
      if (frameRef.current) cancelAnimationFrame(frameRef.current);
    };
  }, []);

  const handleClick = (e: React.MouseEvent) => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const now = performance.now();
    for (let i = 0; i < sparkCount; i++) {
      sparksRef.current.push({
        x: e.clientX,
        y: e.clientY,
        angle: (2 * Math.PI * i) / sparkCount,
        startTime: now,
      });
    }
    if (!frameRef.current) frameRef.current = requestAnimationFrame(draw);
  };

  return (
    <div
      style={{ position: "relative", width: "100%", height: "100%" }}
      onClickCapture={handleClick}
    >
      {children}
      <canvas
        ref={canvasRef}
        style={{
          position: "fixed",
          inset: 0,
          pointerEvents: "none",
          zIndex: 100000,
        }}
      />
    </div>
  );
};

export default ClickSpark;
